import type { DiscoveryEvent, DiscoveryEventDetail, UpdateEventRequest } from '@/lib/api/apiTypes';

export type RadiousEvent = DiscoveryEvent;
export type RadiousEventDetail = DiscoveryEventDetail;
export type EventUpdateInput = UpdateEventRequest;

const EVENT_PALETTES: [string, string][] = [
  ['#0f172a', '#10b981'],
  ['#431407', '#fb923c'],
  ['#082f49', '#38bdf8'],
  ['#4c0519', '#fb7185'],
  ['#3f6212', '#84cc16'],
  ['#701a75', '#f0abfc'],
  ['#083344', '#67e8f9'],
  ['#78350f', '#fcd34d'],
  ['#312e81', '#a78bfa'],
];

const DEFAULT_EVENT_LIVE_DURATION_HOURS = 4;
const ROUTE_LABEL_MAX_LENGTH = 72;

export function getEventPalette(eventID: string): [string, string] {
  let hash = 0;
  for (let index = 0; index < eventID.length; index += 1) {
    hash = (hash * 31 + eventID.charCodeAt(index)) >>> 0;
  }
  return EVENT_PALETTES[hash % EVENT_PALETTES.length];
}

export function formatEventDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  }).format(new Date(value));
}

export function formatEventTime(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

export function formatEventTimeRange(startsAt: string, endsAt?: string | null) {
  if (!endsAt) {
    return formatEventTime(startsAt);
  }
  return `${formatEventTime(startsAt)} - ${formatEventTime(endsAt)}`;
}

export function getEventLiveCutoffMs(startsAt: string, endsAt?: string | null) {
  if (endsAt) {
    return new Date(endsAt).getTime();
  }
  return new Date(startsAt).getTime() + DEFAULT_EVENT_LIVE_DURATION_HOURS * 60 * 60 * 1000;
}

export function formatEventDayLabel(value: string) {
  const date = new Date(value);
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfTarget = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86400000);

  if (diffDays === 0) {
    return 'Tonight';
  }
  if (diffDays === 1) {
    return 'Tomorrow';
  }
  if (diffDays > 1 && diffDays < 7) {
    return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDistanceLabel(distanceMiles: number) {
  if (!distanceMiles || Number.isNaN(distanceMiles)) {
    return 'Nearby';
  }
  if (distanceMiles < 0.1) {
    return '<0.1 mi';
  }
  if (distanceMiles < 10) {
    return `${distanceMiles.toFixed(1)} mi`;
  }
  return `${Math.round(distanceMiles)} mi`;
}

export type RelativeTimeResult = {
  label: string;
  color: string;
  urgent: boolean;
};

export function getRelativeTime(
  startsAt: string,
  endsAt?: string | null,
  now: Date = new Date()
): RelativeTimeResult {
  const start = new Date(startsAt);
  const end = new Date(getEventLiveCutoffMs(startsAt, endsAt));
  const diffMs = start.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (now > end) {
    return { label: 'Ended', color: '#6a6460', urgent: false };
  }

  if (diffMins <= 0 && now < end) {
    return { label: 'Happening now', color: '#1D9E75', urgent: true };
  }

  if (diffMins <= 30) {
    return { label: `Starting in ${diffMins}m`, color: '#ef4e2e', urgent: true };
  }

  if (diffMins <= 60) {
    return { label: `Starting in ${diffMins}m`, color: '#ef4e2e', urgent: false };
  }

  if (diffMins <= 1440) {
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (mins === 0) {
      return { label: `In ${hours}h`, color: '#b0a898', urgent: false };
    }
    return { label: `In ${hours}h ${mins}m`, color: '#b0a898', urgent: false };
  }

  return {
    label: `${formatEventDayLabel(startsAt)} · ${new Date(startsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
    color: '#6a6460',
    urgent: false,
  };
}

export function formatRelativeTime(value: string) {
  const date = new Date(value);
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const RelativeTimeFormat =
    typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl ? Intl.RelativeTimeFormat : null;

  if (!RelativeTimeFormat) {
    if (Math.abs(diffMinutes) < 60) {
      return formatRelativeFallback(diffMinutes, 'minute');
    }

    const diffHours = Math.round(diffMinutes / 60);
    if (Math.abs(diffHours) < 24) {
      return formatRelativeFallback(diffHours, 'hour');
    }

    const diffDays = Math.round(diffHours / 24);
    return formatRelativeFallback(diffDays, 'day');
  }

  const rtf = new RelativeTimeFormat('en', { numeric: 'auto' });

  if (Math.abs(diffMinutes) < 60) {
    return rtf.format(diffMinutes, 'minute');
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  }

  const diffDays = Math.round(diffHours / 24);
  return rtf.format(diffDays, 'day');
}

function formatRelativeFallback(value: number, unit: 'minute' | 'hour' | 'day') {
  if (value === 0) {
    return 'just now';
  }

  const absoluteValue = Math.abs(value);
  const label = absoluteValue === 1 ? unit : `${unit}s`;

  if (value > 0) {
    return `in ${absoluteValue} ${label}`;
  }

  return `${absoluteValue} ${label} ago`;
}

